import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Col, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Btn, H3 } from '../../../AbstractElements';
import { get, postJson } from '../../../hook/request';
import Contatti from './Contatti';

// -------------------- TYPES --------------------

type AziendaApi = {
  ID: number;
  nome: string;
  dominio: string;
  dominio_url: string;
  dominio_nome: string;
  dominio_title: string;
  email_ordini: string | null; // CSV lato API in GET
  email_customer: string | null;
  email_info: string | null;
  email_contatti: string | null;
  email_disponibilita: string | null;
  email_supporto: string | null;
  email_alert: string | null;
  email_web: string | null;
  pec: string | null;
  telefono: string | null;
  telefono_1: string | null;
  corriere: string | null;
  piva: string | null;
  rea: string | null;
  indirizzo: string | null;
  orari: string | null;
  url_maps: string | null;
  codice: string;
  tipo: string;
  meta?: unknown; // string JSON o object
};

type Lang = {
  ID: number | string;
  nome: string;
  classe: string;
  codice: string; // it, en, fr, ...
  abilitato: 0 | 1;
};

// I18n campi Dominio
export type MetaI18n = Record<string, {
  payoff?: string;
  description?: string;
  iban?: string;
  mailup_lista?: string;
  mailup_gruppo_sito?: string;
  mailup_gruppo_newsletter?: string;
}>;

// Stato form semplice
type FormAzienda = {
  // Azienda
  nome: string;
  partita_iva: string;
  rea: string;
  // Contatti
  indirizzo: string;
  url_google_maps: string;
  email_ordini: string[]; // multi
  pec: string;
  email_customer: string;
  email_info: string;
  email_supporto: string;
  email_disponibilita: string;
  email_alert: string;
  email_web: string;
  telefono: string;
  telefono_1: string;
  orari_customer: string;
  corriere: string;
  // Dominio
  dominio: string;
  dominio_url: string;
  dominio_nome: string;
  dominio_title: string;
};

// -------------------- HELPERS --------------------

function parseMeta(meta: unknown): MetaI18n {
  if (!meta) return {};
  try {
    if (typeof meta === 'string') return JSON.parse(meta);
    if (typeof meta === 'object') return meta as MetaI18n;
  } catch (e) {}
  return {};
}

function normalizeCSV(s: string | null | undefined): string[] {
  if (!s) return [];
  return s
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
}

// -------------------- EMAIL CHIPS --------------------

function EmailChips({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled?: boolean; }) {
  const [draft, setDraft] = useState('');

  const add = (email: string) => {
    const e = email.trim();
    if (!e) return;
    if (value.includes(e)) return;
    onChange([...value, e]);
    setDraft('');
  };

  const remove = (email: string) => onChange(value.filter(v => v !== email));

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (ev) => {
    if (ev.key === 'Enter' || ev.key === ',') {
      ev.preventDefault();
      add(draft);
    }
    if (ev.key === 'Backspace' && !draft && value.length) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div className="border rounded p-2" style={{ minHeight: 40 }}>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {value.map(mail => (
          <span key={mail} className="badge bg-secondary d-flex align-items-center" style={{ fontSize: 12 }}>
            {mail}
            {!disabled && (
              <button type="button" className="btn btn-link btn-sm text-white ms-1 p-0" onClick={() => remove(mail)} aria-label={`rimuovi ${mail}`}>
                ×
              </button>
            )}
          </span>
        ))}
        <input
          className="border-0 flex-grow-1"
          style={{ outline: 'none' }}
          placeholder="Scrivi un'email e premi Invio"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
      </div>
    </div>
  );
}


export default function AziendaForm() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [languages, setLanguages] = useState<Lang[]>([]);
  const enabledLangCodes = useMemo(() => languages.filter(l => l.abilitato === 1).map(l => (l.codice || '').toLowerCase()), [languages]);
  const [activeLang, setActiveLang] = useState('it');
  const [i18n, setI18n] = useState<MetaI18n>({});

  const [form, setForm] = useState<FormAzienda>({
    nome: '',
    partita_iva: '',
    rea: '',
    indirizzo: '',
    url_google_maps: '',
    email_ordini: [],
    pec: '',
    email_customer: '',
    email_info: '',
    email_supporto: '',
    email_disponibilita: '',
    email_alert: '',
    email_web: '',
    telefono: '',
    telefono_1: '',
    orari_customer: '',
    corriere: '',
    dominio: '',
    dominio_url: '',
    dominio_nome: '',
    dominio_title: '',
  });

  // Load languages and data
  useEffect(() => {
    (async () => {
      try {
        const langs = await get<Lang[]>('/languages');
        const ll = (langs as any)?.data ?? langs;
        setLanguages(Array.isArray(ll) ? ll : []);
      } catch {}

      try {
        const res = await get<AziendaApi[]>('/azienda/edit');
        // Supporta sia AxiosResponse { data: { status, data: [] } } che plain { status, data: [] }
        const root: any = (res as any)?.data ?? res; // axios -> {status, data}
        const arr: any = Array.isArray(root)
          ? root
          : Array.isArray(root?.data)
          ? root.data
          : Array.isArray(root?.data?.data)
          ? root.data.data
          : [];
        const data: AziendaApi | undefined = Array.isArray(arr) ? arr[0] : undefined;
        if (!data) throw new Error('Nessun dato azienda');

        setForm({
          nome: data.nome ?? '',
          partita_iva: data.piva ?? '',
          rea: data.rea ?? '',
          indirizzo: data.indirizzo ?? '',
          url_google_maps: data.url_maps ?? '',
          email_ordini: normalizeCSV(data.email_ordini),
          pec: data.pec ?? '',
          email_customer: data.email_customer ?? '',
          email_info: data.email_info ?? '',
          email_supporto: data.email_supporto ?? '',
          email_disponibilita: data.email_disponibilita ?? '',
          email_alert: data.email_alert ?? '',
          email_web: data.email_web ?? '',
          telefono: data.telefono ?? '',
          telefono_1: data.telefono_1 ?? '',
          orari_customer: data.orari ?? '',
          corriere: data.corriere ?? '',
          dominio: data.dominio ?? '',
          dominio_url: data.dominio_url ?? '',
          dominio_nome: data.dominio_nome ?? '',
          dominio_title: data.dominio_title ?? '',
        });

        const parsed = parseMeta(data.meta);
        setI18n(parsed);
        const firstLang = Object.keys(parsed)[0] || 'it';
        setActiveLang(firstLang.toLowerCase());
      } catch (e: any) {
        toast.error(e?.message || 'Errore nel caricamento');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const setI18nField = (lang: string, field: keyof Required<MetaI18n>[string], value: string) => {
    setI18n(prev => ({
      ...prev,
      [lang]: {
        payoff: '',
        description: '',
        iban: '',
        mailup_lista: '',
        mailup_gruppo_sito: '',
        mailup_gruppo_newsletter: '',
        ...(prev[lang] || {}),
        [field]: value,
      },
    }));
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payloadI18n: Record<string, string> = {};
    const codes = enabledLangCodes.length ? enabledLangCodes : ['it'];

    for (const code of codes) {
      const lang = code.toLowerCase();
      const by = i18n[lang] || {};
      payloadI18n[`payoff_${lang}`] = by.payoff ?? '';
      payloadI18n[`description_${lang}`] = by.description ?? '';
      payloadI18n[`iban_${lang}`] = by.iban ?? '';
      payloadI18n[`mailup_lista_${lang}`] = by.mailup_lista ?? '';
      payloadI18n[`mailup_gruppo_sito_${lang}`] = by.mailup_gruppo_sito ?? '';
      payloadI18n[`mailup_gruppo_newsletter_${lang}`] = by.mailup_gruppo_newsletter ?? '';
    }

    const payload = {
      nome: form.nome,
      partita_iva: form.partita_iva,
      rea: form.rea,
      contatti: {
        indirizzo: form.indirizzo,
        url_google_maps: form.url_google_maps,
        email_ordini: form.email_ordini,
        pec: form.pec,
        email_customer: form.email_customer,
        email_info: form.email_info,
        email_supporto: form.email_supporto,
        email_disponibilita: form.email_disponibilita,
        email_alert: form.email_alert,
        email_web: form.email_web,
        telefono: form.telefono,
        telefono_1: form.telefono_1,
        orari_customer: form.orari_customer,
        corriere: form.corriere,
      },
      dominio: {
        dominio: form.dominio,
        dominio_url: form.dominio_url,
        dominio_nome: form.dominio_nome,
        dominio_title: form.dominio_title,
      },
      ...payloadI18n,
    };

    try {
      await postJson('/azienda/dati', payload);
      toast.success('Dati azienda aggiornati!');
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Errore salvataggio';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-body pt-4">
        <Col xl={12}>
          <Card>
            <CardBody className="d-flex justify-content-center py-5"><Spinner /></CardBody>
          </Card>
        </Col>
      </div>
    );
  }

  return (
    <div className="page-body pt-4">
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <div className='mb-3'>
                <H3>Gestione Azienda</H3>
            </div>
            <Form onSubmit={handleSubmit}>
           
              <h5 className="mb-3">Azienda</h5>
              <Row className="g-3">
                <Col md={6}>
                  <Label htmlFor="nome">Nome</Label>
                  <Input id="nome" name="nome" value={form.nome} onChange={onChange} />
                </Col>
                <Col md={3}>
                  <Label htmlFor="partita_iva">Partita IVA</Label>
                  <Input id="partita_iva" name="partita_iva" value={form.partita_iva} onChange={onChange} />
                </Col>
                <Col md={3}>
                  <Label htmlFor="rea">REA</Label>
                  <Input id="rea" name="rea" value={form.rea} onChange={onChange} />
                </Col>
              </Row>

              <hr className="my-4" />

              {/* ############### CONTATTI ############### */}
              <h5 className="mb-3">Contatti</h5>
              <Row className="g-3">
                <Col md={12}>
                  <Label htmlFor="indirizzo">Indirizzo</Label>
                  <Input id="indirizzo" name="indirizzo" value={form.indirizzo} onChange={onChange} />
                </Col>
                <Col md={12}>
                  <Label htmlFor="url_google_maps">URL Google Maps</Label>
                  <Input id="url_google_maps" name="url_google_maps" value={form.url_google_maps} onChange={onChange} />
                </Col>
                <Col md={12}>
                  <Label>Email ordini</Label>
                  <EmailChips value={form.email_ordini} onChange={(v) => setForm(p => ({ ...p, email_ordini: v }))} />
                </Col>
                <Col md={6}><Label htmlFor="pec">PEC</Label><Input id="pec" name="pec" value={form.pec} onChange={onChange} /></Col>
                {/* <Col md={6}><Label htmlFor="email_customer">Email customer</Label><Input id="email_customer" name="email_customer" value={form.email_customer} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="email_info">Email info</Label><Input id="email_info" name="email_info" value={form.email_info} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="email_supporto">Email supporto</Label><Input id="email_supporto" name="email_supporto" value={form.email_supporto} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="email_disponibilita">Email disponibilità</Label><Input id="email_disponibilita" name="email_disponibilita" value={form.email_disponibilita} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="email_alert">Email alert</Label><Input id="email_alert" name="email_alert" value={form.email_alert} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="email_web">Email web</Label><Input id="email_web" name="email_web" value={form.email_web} onChange={onChange} /></Col> */}
                <Col md={3}><Label htmlFor="telefono">Telefono</Label><Input id="telefono" name="telefono" value={form.telefono} onChange={onChange} /></Col>
                <Col md={3}><Label htmlFor="telefono_1">Telefono 1</Label><Input id="telefono_1" name="telefono_1" value={form.telefono_1} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="orari_customer">Orari customer</Label><Input id="orari_customer" name="orari_customer" value={form.orari_customer} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="corriere">Corriere</Label><Input id="corriere" name="corriere" value={form.corriere} onChange={onChange} /></Col>
                <Contatti />
              </Row>

              <hr className="my-4" />

              <h5 className="mb-3">Dominio</h5>
              <Row className="g-3">
                <Col md={6}><Label htmlFor="dominio">Dominio</Label><Input id="dominio" name="dominio" value={form.dominio} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="dominio_url">Dominio URL</Label><Input id="dominio_url" name="dominio_url" value={form.dominio_url} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="dominio_nome">Dominio Nome</Label><Input id="dominio_nome" name="dominio_nome" value={form.dominio_nome} onChange={onChange} /></Col>
                <Col md={6}><Label htmlFor="dominio_title">Dominio Title</Label><Input id="dominio_title" name="dominio_title" value={form.dominio_title} onChange={onChange} /></Col>
              </Row>

              {/* Tabs lingue */}
              <div className="mb-3 mt-4">
                <ul className="nav nav-tabs">
                  {(enabledLangCodes.length ? enabledLangCodes : ['it']).map(code => {
                    const up = code.toUpperCase();
                    return (
                      <li className="nav-item" key={code}>
                        <a href="#" className={`nav-link ${activeLang.toUpperCase() === up ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); setActiveLang(code); }}>{up}</a>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Campi i18n per lingua attiva */}
              <Row className="g-3">
                <Col md={6}>
                  <Label>Dominio Payoff ({activeLang.toUpperCase()})</Label>
                  <Input value={i18n[activeLang]?.payoff ?? ''} onChange={(e) => setI18nField(activeLang, 'payoff', e.target.value)} />
                </Col>
                <Col md={6}>
                  <Label>IBAN ({activeLang.toUpperCase()})</Label>
                  <Input value={i18n[activeLang]?.iban ?? ''} onChange={(e) => setI18nField(activeLang, 'iban', e.target.value)} />
                </Col>
                <Col md={12}>
                  <Label>Descrizione ({activeLang.toUpperCase()})</Label>
                  <Input type="textarea" value={i18n[activeLang]?.description ?? ''} onChange={(e) => setI18nField(activeLang, 'description', e.target.value)} />
                </Col>
                <Col md={4}>
                  <Label>MailUp - lista ({activeLang.toUpperCase()})</Label>
                  <Input value={i18n[activeLang]?.mailup_lista ?? ''} onChange={(e) => setI18nField(activeLang, 'mailup_lista', e.target.value)} />
                </Col>
                <Col md={4}>
                  <Label>MailUp - Gruppo al sito ({activeLang.toUpperCase()})</Label>
                  <Input value={i18n[activeLang]?.mailup_gruppo_sito ?? ''} onChange={(e) => setI18nField(activeLang, 'mailup_gruppo_sito', e.target.value)} />
                </Col>
                <Col md={4}>
                  <Label>MailUp - iscrizione newsletter ({activeLang.toUpperCase()})</Label>
                  <Input value={i18n[activeLang]?.mailup_gruppo_newsletter ?? ''} onChange={(e) => setI18nField(activeLang, 'mailup_gruppo_newsletter', e.target.value)} />
                </Col>
              </Row>

              <Col xs={12} className="mt-4 d-flex align-items-center gap-2">
                <Btn className="px-4" color="primary" type="submit" disabled={saving}>Aggiorna</Btn>
                {saving && <Spinner size="sm" />}
              </Col>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}
